import React, { useState, useEffect } from 'react';
import Tippy from '@tippyjs/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFile, faClockRotateLeft, faCalendar,
    faCodeBranch, faLayerGroup, faFileLines,
    faEdit, faXmark, faEye,
    faSpinner,
    faSave,
    faXmarkCircle,
    faRobot,
    faCircleCheck,
    faCalendarCheck,
    faEyeSlash
} from '@fortawesome/free-solid-svg-icons';
import LoadingSpinner from '../Common/LoadingSpinner';
import './DocumentDetailsModal.css';
import { formatDate } from '../../utils/textUtils';
import apiHelper from '../../utils/apiHelper';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { repositoryService } from '../../services/repositoryService';

// PDF.js worker is configured in UploadDocumentModal.js and RepositoryBrowser.js

const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
const currentYear = new Date().getFullYear();
const years = Array.from({ length: currentYear - 2000 + 6 }, (_, i) => 2000 + i);

const getQuarterFromMonth = (month) => {
    if (!month) return '';

    // Convert short month name to full month name
    const monthMap = {
        'Jan': 'January', 'Feb': 'February', 'Mar': 'March',
        'Apr': 'April', 'May': 'May', 'Jun': 'June',
        'Jul': 'July', 'Aug': 'August', 'Sep': 'September',
        'Oct': 'October', 'Nov': 'November', 'Dec': 'December'
    };

    const fullMonth = monthMap[month] || month;
    const monthIndex = months.indexOf(fullMonth);

    if (monthIndex === -1) {
        console.warn(`Invalid month format: ${month}`);
        return '';
    }

    return `Q${Math.floor(monthIndex / 3) + 1}`;
};

const DocumentDetailsModal = ({ document, onClose, onEdit, isQdrantDocument = false }) => {
    // COMPREHENSIVE FRONTEND DEBUG

    const [pdfUrl, setPdfUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isViewerOpen, setIsViewerOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({
        title: document?.title || '',
        fileName: document?.fileName || '',
        workingGroup: document?.workingGroupName || document?.workingGroup || '',
        type: document?.type || '',
        mpKey: document?.mpKey || '',
        summary: document?.summary || '',
        qtr: document?.qtr || '',
        month: document?.month || '',
        year: document?.year || '',
        publish: document?.publish || false,
        publishDate: document?.publishDate || '',
        doNotShowInSources: document?.doNotShowInSources || false
    });



    const [errors, setErrors] = useState({});
    const [documentTypes, setDocumentTypes] = useState([]);
    const [workingGroups, setWorkingGroups] = useState([]);
    const [mpKeyOptions, setMpKeyOptions] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);

    const fetchPdf = async () => {

        if (!document) return;

        setIsLoading(true);
        setError(null);

        try {
            let response;
            if (document.isIndexed) {
                // For indexed documents, use the document ID directly
                response = await apiHelper.fetchPDFNameAndContentById(document.id);
            } else {
                // For regular repository documents, use the ID
                response = await apiHelper.fetchPDFNameAndContentById(document.id);
            }

            if (response?.fileContent) {
                const pdfBlob = new Blob([response.fileContent], { type: 'application/pdf' });
                const pdfBlobUrl = URL.createObjectURL(pdfBlob);
                setPdfUrl(pdfBlobUrl);
            } else {
                throw new Error('No PDF content received');
            }
        } catch (err) {
            console.error("Error loading PDF:", err.message);
            setError('Failed to load PDF');
        } finally {
            setIsLoading(false);
        }
    };

    // Cleanup function for the PDF URL
    useEffect(() => {
        return () => {
            if (pdfUrl) {
                URL.revokeObjectURL(pdfUrl);
            }
        };
    }, [pdfUrl]);

    useEffect(() => {
        const fetchChoices = async () => {


            try {
                const [mpKeys, docTypes, workGroups] = await Promise.all([
                    repositoryService.getMPKeyChoices(),
                    repositoryService.getDocumentTypeChoices(),
                    repositoryService.getWorkingGroupChoices()
                ]);


                setMpKeyOptions(mpKeys);
                setDocumentTypes(docTypes);
                setWorkingGroups(workGroups);
            } catch (error) {
                console.error('Error fetching choices:', error);
            }
        };

        fetchChoices();
    }, []);

    const handleEdit = () => {


        setFormData({
            title: document.title,
            fileName: document.fileName,
            workingGroup: document.workingGroupName || document.workingGroup || '',
            type: document.type,
            mpKey: document.mpKey,
            summary: document.summary,
            qtr: document.qtr,
            month: document.month,
            year: document.year,
            publish: document.publish,
            publishDate: document.publishDate,
            doNotShowInSources: document.doNotShowInSources || false
        });



        setIsEditing(true);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'month') {
            // Always calculate quarter when month changes
            const calculatedQuarter = getQuarterFromMonth(value);
            setFormData(prev => ({
                ...prev,
                [name]: value,
                qtr: calculatedQuarter
            }));
        } else if (name === 'mpKey') {
            setFormData(prev => ({
                ...prev,
                [name]: value,
                qtr: ['CBI_QTRLY', 'KEY_POLICY_MESSAGES'].includes(value)
                    ? getQuarterFromMonth(prev.month)
                    : ''
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.title.trim()) {
            newErrors.title = 'Title is required';
        }
        if (!formData.type) {
            newErrors.type = 'Document type is required';
        }
        if (!formData.year) {
            newErrors.year = 'Year is required';
        }
        if (['CBI_QTRLY', 'KEY_POLICY_MESSAGES'].includes(formData.mpKey)) {
            if (!formData.qtr) {
                newErrors.qtr = 'Quarter is required for this MP Key';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            toast.error('Please fix validation errors');
            return;
        }

        try {
            setIsSubmitting(true);

            // Update the document
            const response = await repositoryService.updateDocument(document.id, {
                ...formData,
                id: document.id
            });

            if (response.success) {
                // Update the local document state with the new data
                const updatedDocument = {
                    ...document,
                    ...formData,  // Update with form data
                    modified: new Date().toISOString() // Update modified date
                };

                toast.success('Document updated successfully');
                onEdit(updatedDocument);  // Pass the updated document to parent
            } else {
                throw new Error(response.message || 'Failed to update document');
            }
        } catch (error) {
            console.error('Save error:', error);
            toast.error(error.message || 'Failed to update document');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleGenerateSummary = async () => {
        setIsGeneratingSummary(true);
        try {
            // Get the document ID for summary generation
            const headers = await apiHelper.getHeaders('daire');

            const aiResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/daire/summariseContent`, {
                method: 'POST',
                headers: {
                    ...headers,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    documentId: document.id,
                    title: document.title,
                    type: "document"
                })
            });

            if (!aiResponse.ok) {
                throw new Error('Failed to generate summary');
            }

            const data = await aiResponse.json();

            setFormData(prev => ({
                ...prev,
                summary: data.summary.choices[0].message.content,
            }));

            toast.success('Summary generated successfully!');
        } catch (error) {
            console.error('Summary generation error:', error);
            toast.error('Failed to generate summary: ' + error.message);
        } finally {
            setIsGeneratingSummary(false);
        }
    };

    // Add this to your existing JSX, in the actions section:
    const renderViewerContent = () => {
        if (isLoading) {
            return (
                <div className="pdf-loading">
                    <FontAwesomeIcon icon={faSpinner} spin />
                </div>
            );
        }

        if (error) {
            return (
                <div className="pdf-error">
                    <span>{error}</span>
                    <button onClick={fetchPdf}>Retry</button>
                </div>
            );
        }

        if (pdfUrl) {
            return (
                <iframe
                    src={pdfUrl}
                    title={document.title}
                    className="pdf-viewer"
                />
            );
        }

        return null;
    };

    // Modify the content section when in edit mode:
    const renderEditableContent = () => (
        <div className="modal-content">
            <div className="metadata-section">
                <h3>Document Details</h3>
                <div className="form-group">
                    <label htmlFor="title">Title*</label>
                    <input
                        type="text"
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        className={errors.title ? 'error' : ''}
                    />
                    {errors.title && <span className="error-message">{errors.title}</span>}
                </div>



                <div className="form-row">
                    <div className="form-group">
                        <label>Document Type</label>
                        <select
                            name="type"
                            value={formData.type}
                            onChange={handleChange}
                        >
                            <option value="">Select Type</option>
                            {documentTypes.map(type => (
                                <option key={type} value={type}>{type}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Working Group</label>
                        <select
                            name="workingGroup"
                            value={formData.workingGroup}
                            onChange={handleChange}
                        >
                            <option value="">Select Working Group</option>
                            {workingGroups.map(group => (
                                <option key={group} value={group}>{group}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>Month</label>
                        <select
                            name="month"
                            value={formData.month}
                            onChange={handleChange}
                        >
                            <option value="">Select Month</option>
                            {months.map(month => (
                                <option key={month} value={month}>{month}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Year *</label>
                        <select
                            name="year"
                            value={formData.year}
                            onChange={handleChange}
                            required
                        >
                            {years.map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>MP Key</label>
                        <select
                            name="mpKey"
                            value={formData.mpKey}
                            onChange={handleChange}
                        >
                            <option value="">Select MP Key</option>
                            {mpKeyOptions.map(key => (
                                <option key={key} value={key}>{key}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                name="doNotShowInSources"
                                checked={formData.doNotShowInSources}
                                onChange={(e) => handleChange({
                                    target: {
                                        name: 'doNotShowInSources',
                                        value: e.target.checked
                                    }
                                })}
                            />
                            Do Not Show In Sources
                        </label>
                        <small className="field-description">
                            When enabled, this document will not appear in source listings
                        </small>
                    </div>
                </div>

                {['CBI_QTRLY', 'KEY_POLICY_MESSAGES'].includes(formData.mpKey) && (
                    <div className="form-row">
                        <div className="form-group">
                            <label>Quarter *</label>
                            <select
                                name="qtr"
                                value={formData.qtr}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select Quarter</option>
                                {quarters.map(q => (
                                    <option key={q} value={q}>{q}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}

                <div className="form-row publish-row">
                    <div className="form-group">
                        <label>Status</label>
                        <select
                            name="publish"
                            value={formData.publish ? 'Published' : 'Draft'}
                            onChange={(e) => {
                                handleChange({
                                    target: {
                                        name: 'publish',
                                        value: e.target.value === 'Published'
                                    }
                                });
                            }}
                            className="status-select"
                        >
                            <option value="Published">Published</option>
                            <option value="Draft">Draft</option>
                        </select>
                    </div>

                    {formData.publish && (
                        <div className="form-group publish-date">
                            <label>Publish Date</label>
                            <input
                                type="date"
                                name="publishDate"
                                value={formData.publishDate || ''}
                                onChange={handleChange}
                                min={new Date().toISOString().split('T')[0]}
                            />
                        </div>
                    )}
                </div>
            </div>

            <div className="metadata-section">
                <h3>Summary</h3>
                <div className="summary-container">
                    <textarea
                        name="summary"
                        value={formData.summary}
                        onChange={handleChange}
                        placeholder="Enter document summary"
                        rows={3}
                        disabled={isGeneratingSummary}
                    />
                    <button
                        type="button"
                        className="generate-summary-btn"
                        onClick={handleGenerateSummary}
                        disabled={isGeneratingSummary}
                    >
                        <FontAwesomeIcon
                            icon={isGeneratingSummary ? faSpinner : faRobot}
                            spin={isGeneratingSummary}
                        />
                        {isGeneratingSummary ? 'Generating...' : 'Generate Summary with Daire'}
                    </button>
                </div>
            </div>
        </div>
    );

    if (!document) return null;

    return (
        <>
            <div className="modal-overlay" onClick={onClose}></div>
            <div className="document-details-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{isEditing ? 'Edit Document' : 'Viewing Document'}</h2>
                    <div className="modal-actions" onClick={(e) => e.stopPropagation()}>
                        {isEditing ? (
                            <>
                                <button
                                    className="action-button save"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleSave();
                                    }}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <FontAwesomeIcon icon={faSpinner} spin /> Saving...
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faSave} /> Save
                                        </>
                                    )}
                                </button>
                                <button
                                    className="action-button cancel"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsEditing(false);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faXmarkCircle} /> Cancel
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    className="action-button view"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsViewerOpen(true);
                                        fetchPdf();
                                    }}
                                >
                                    <FontAwesomeIcon icon={faEye} /> View
                                </button>
                                <button
                                    className="action-button edit"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleEdit();
                                    }}
                                >
                                    <FontAwesomeIcon icon={faEdit} /> Edit
                                </button>
                            </>
                        )}
                        <button
                            className="modal-close"
                            onClick={(e) => {
                                e.stopPropagation();
                                onClose();
                            }}
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </button>
                    </div>
                </div>

                <div className="modal-content">
                    {isEditing ? (
                        // Edit Mode
                        renderEditableContent()
                    ) : (
                        // View Mode
                        <>
                            {/* Document Details */}
                            <div className="metadata-section">
                                <h3>Document Details</h3>

                                {/* Title in its own row */}
                                <div className="filename-row">
                                    <div className="metadata-item full-width">
                                        <FontAwesomeIcon icon={faFileLines} />
                                        <div className="metadata-content">
                                            <label>Title</label>
                                            <Tippy content={document.title} placement="top" delay={[300, 0]} animation="fade">
                                                <span>{document.title || 'Untitled'}</span>
                                            </Tippy>
                                        </div>
                                    </div>
                                </div>

                                {/* Add Working Group here, right after title */}
                                <div className="filename-row">
                                    <div className="metadata-item full-width">
                                        <FontAwesomeIcon icon={faLayerGroup} />
                                        <div className="metadata-content">
                                            <label>Working Group</label>
                                            <Tippy content={document.workingGroupName || document.workingGroup} placement="top" delay={[300, 0]} animation="fade">
                                                <span className="working-group-name">{document.workingGroupName || document.workingGroup || 'None'}</span>
                                            </Tippy>
                                        </div>
                                    </div>
                                </div>

                                {/* Document metadata grid */}
                                <div className="document-metadata">
                                    <div className="metadata-item">
                                        <FontAwesomeIcon icon={faClockRotateLeft} />
                                        <div className="metadata-content">
                                            <label>Created</label>
                                            <span>{formatDate(document.created, true)}</span>
                                        </div>
                                    </div>
                                    <div className="metadata-item">
                                        <FontAwesomeIcon icon={faCalendar} />
                                        <div className="metadata-content">
                                            <label>Modified</label>
                                            <span>{formatDate(document.modified, true)}</span>
                                        </div>
                                    </div>
                                    <div className="metadata-item">
                                        <FontAwesomeIcon icon={faCodeBranch} />
                                        <div className="metadata-content">
                                            <label>Version</label>
                                            <span>{document.version || '1.0'}</span>
                                        </div>
                                    </div>
                                    <div className="metadata-item">
                                        <FontAwesomeIcon icon={faCircleCheck} />
                                        <div className="metadata-content">
                                            <label>Status</label>
                                            <span>{document.publish ? 'Published' : 'Draft'}</span>
                                        </div>
                                    </div>

                                    {document.publishDate && (
                                        <div className="metadata-item">
                                            <FontAwesomeIcon icon={faCalendarCheck} />
                                            <div className="metadata-content">
                                                <label>Publish Date</label>
                                                <span>{formatDate(document.publishDate)}</span>
                                            </div>
                                        </div>
                                    )}
                                    <div className="metadata-item">
                                        <FontAwesomeIcon icon={document.doNotShowInSources ? faEyeSlash : faEye} />
                                        <div className="metadata-content">
                                            <label>Show In Sources</label>
                                            <span className={`status-badge ${document.doNotShowInSources ? 'hidden' : 'visible'}`}>
                                                {document.doNotShowInSources ? 'Hidden' : 'Visible'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Summary Section */}
                            <div className="document-summary">
                                <h3>Summary</h3>
                                <p>{document.summary || 'No summary available.'}</p>
                            </div>

                            {/* File Information */}
                            <div className="metadata-section">
                                <h3>File Information</h3>

                                {/* Filename row */}
                                <div className="filename-row">
                                    <div className="metadata-item full-width">
                                        <FontAwesomeIcon icon={faFile} />
                                        <div className="metadata-content">
                                            <label>File Name</label>
                                            <Tippy content={document.fileName} placement="top" delay={[300, 0]} animation="fade">
                                                <span>{document.fileName || 'Unknown'}</span>
                                            </Tippy>
                                        </div>
                                    </div>
                                </div>

                                {/* File details grid */}
                                <div className="document-metadata">
                                    <div className="metadata-item">
                                        <FontAwesomeIcon icon={faLayerGroup} />
                                        <div className="metadata-content">
                                            <label>Working Group</label>
                                            <span>{document.workingGroupName || document.workingGroup || 'None'}</span>
                                        </div>
                                    </div>
                                    <div className="metadata-item">
                                        <FontAwesomeIcon icon={faFileLines} />
                                        <div className="metadata-content">
                                            <label>Type</label>
                                            <span>{document.type || 'Document'}</span>
                                        </div>
                                    </div>
                                    <div className="metadata-item">
                                        <FontAwesomeIcon icon={faCodeBranch} />
                                        <div className="metadata-content">
                                            <label>MP Key</label>
                                            <Tippy content={document.mpKey} placement="top" delay={[300, 0]} animation="fade">
                                                <span>{document.mpKey || 'None'}</span>
                                            </Tippy>
                                        </div>
                                    </div>
                                    <div className="metadata-item">
                                        <FontAwesomeIcon icon={faCalendar} />
                                        <div className="metadata-content">
                                            <label>Month</label>
                                            <span>{document.month || 'None'}</span>
                                        </div>
                                    </div>
                                    <div className="metadata-item">
                                        <FontAwesomeIcon icon={faCalendar} />
                                        <div className="metadata-content">
                                            <label>Year</label>
                                            <span>{document.year || 'None'}</span>
                                        </div>
                                    </div>
                                    {document.mpKey && ['CBI_QTRLY', 'KEY_POLICY_MESSAGES'].includes(document.mpKey) && (
                                        <div className="metadata-item">
                                            <FontAwesomeIcon icon={faCalendar} />
                                            <div className="metadata-content">
                                                <label>Quarter</label>
                                                <span>{document.qtr || 'None'}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* PDF Viewer Section */}
                {isViewerOpen && (
                    <div className="pdf-viewer-container">
                        <div className="pdf-viewer-header">
                            <h3>Document Preview</h3>
                            <button onClick={() => setIsViewerOpen(false)}>
                                <FontAwesomeIcon icon={faXmark} />
                            </button>
                        </div>
                        <div className="pdf-viewer-content">
                            {isLoading ? (
                                <LoadingSpinner />
                            ) : error ? (
                                <div className="pdf-error">
                                    <p>{error}</p>
                                    <button onClick={fetchPdf}>Retry</button>
                                </div>
                            ) : (
                                <iframe
                                    src={pdfUrl}
                                    title="PDF Viewer"
                                    style={{ border: 'none' }}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default DocumentDetailsModal; 
